import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AttendanceNowDTO, AttendanceReportRowDTO } from '@bifeai/shared';
import { Badge, Card, CardBody, CardHeader, Dialog, EmptyState, Input, Kpi, PageHeader, Select, Skeleton } from '@bifeai/ui';
import { CalendarCheck, Clock, LogIn, UserX } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useSession } from '../../lib/session';
import { todayIso } from '../../lib/units';

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
const hours = (min: number) => `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
const firstOfMonth = () => todayIso().slice(0, 8) + '01';

/**
 * Asistencia (adicional): quién está trabajando ahora en cada sucursal y las horas de cada empleado.
 * La central ve todas las sucursales; cada sucursal, la suya.
 */
export function AttendancePage() {
  const { me } = useSession();
  const enabled = me.tenant.modules.includes('ATTENDANCE');
  const [tab, setTab] = useState<'now' | 'period'>('now');

  if (!enabled) {
    return (
      <>
        <PageHeader title="Asistencia" />
        <Card>
          <EmptyState
            icon={<Clock />}
            title="La asistencia es un adicional"
            text="Cada empleado marca la entrada y la salida con su PIN en el mostrador, y ves quién está trabajando en cada sucursal y las horas de cada uno. Pedile a BifeAI que lo active."
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Asistencia" subtitle="Cada empleado marca la entrada y la salida con su PIN en el Punto de venta (botón del reloj)." />
      <div className="bf-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'now'} onClick={() => setTab('now')}>
          Ahora
        </button>
        <button type="button" role="tab" aria-selected={tab === 'period'} onClick={() => setTab('period')}>
          Horas por período
        </button>
      </div>
      {tab === 'now' ? <NowTab /> : <PeriodTab />}
    </>
  );
}

function NowTab() {
  const { multiBranch } = useSession();
  const q = useQuery({ queryKey: ['attendance', 'now'], queryFn: () => api.get<AttendanceNowDTO[]>('/attendance/now'), refetchInterval: 60_000 });
  const rows = q.data ?? [];
  const inside = rows.filter((r) => r.status === 'IN');
  const absent = rows.filter((r) => r.status === null);
  // Agrupado por sucursal.
  const groups = new Map<string, AttendanceNowDTO[]>();
  for (const r of rows) groups.set(r.branchName ?? '—', [...(groups.get(r.branchName ?? '—') ?? []), r]);

  if (!q.data) return <Skeleton style={{ height: 200 }} />;
  if (!rows.length) return <EmptyState icon={<Clock />} title="No hay empleados cargados" text="Cargalos en Empleados y ponele un PIN a cada uno." />;
  return (
    <>
      <div className="bf-kpi-grid">
        <Kpi label="Trabajando ahora" tone="green" icon={<LogIn />} value={String(inside.length)} />
        <Kpi label="No marcaron hoy" tone="red" icon={<UserX />} value={String(absent.length)} />
      </div>
      {[...groups.entries()].map(([branch, list]) => (
        <Card key={branch}>
          {multiBranch && <CardHeader title={branch} subtitle={`${list.filter((r) => r.status === 'IN').length} trabajando`} />}
          <CardBody flush>
            <table className="bf-table">
              <tbody>
                {list.map((r) => (
                  <tr key={r.employeeId}>
                    <td>
                      <Link to={`/empleados/${r.employeeId}`} className="bf-cell-main">
                        {r.name}
                      </Link>
                      {r.position && <div className="bf-cell-sub">{r.position}</div>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {!r.hasPin ? (
                        <Badge tone="warning">sin PIN: no puede marcar</Badge>
                      ) : r.status === 'IN' ? (
                        <Badge tone="success">Adentro desde {r.since ? timeOf(r.since) : ''}</Badge>
                      ) : r.status === 'OUT' ? (
                        <Badge>Se fue a las {r.since ? timeOf(r.since) : ''}</Badge>
                      ) : (
                        <span className="bf-cell-sub">No marcó hoy</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ))}
    </>
  );
}

function PeriodTab() {
  const { me, multiBranch, isHeadquartersUser } = useSession();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayIso());
  const [branch, setBranch] = useState('');
  const [open, setOpen] = useState<AttendanceReportRowDTO | null>(null);
  const q = useQuery({
    queryKey: ['attendance', 'report', from, to],
    queryFn: () => api.get<AttendanceReportRowDTO[]>(`/attendance/report?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });
  const rows = (q.data ?? []).filter((r) => !branch || r.branchName === branch);

  return (
    <>
      <div className="bf-period">
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Desde <Input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </label>
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Hasta <Input type="date" value={to} min={from} max={todayIso()} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </label>
        {multiBranch && isHeadquartersUser && (
          <Select value={branch} onChange={(e) => setBranch(e.target.value)} aria-label="Sucursal" style={{ maxWidth: 220 }}>
            <option value="">Todas las sucursales</option>
            {me.branches.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
          </Select>
        )}
      </div>
      <Card>
        <CardBody flush>
          {!q.data ? (
            <div style={{ padding: 16 }}>
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={<CalendarCheck />} title="Nadie marcó en estas fechas" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Empleado</th>
                    <th style={{ textAlign: 'right' }}>Días</th>
                    <th style={{ textAlign: 'right' }}>Horas</th>
                    <th style={{ textAlign: 'right' }}>Promedio por día</th>
                    <th style={{ textAlign: 'right' }}>Sin salida</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.employeeId} data-clickable="true" onClick={() => setOpen(r)}>
                      <td>
                        <span className="bf-cell-main">{r.name}</span>
                        {multiBranch && <div className="bf-cell-sub">{r.branchName}</div>}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {r.daysWorked}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                        {r.totalMinutes ? hours(r.totalMinutes) : '—'}
                      </td>
                      <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                        {r.daysWorked ? hours(Math.round(r.totalMinutes / r.daysWorked)) : '—'}
                      </td>
                      <td className={`bf-num ${r.missingOut ? 'bf-text-warning' : 'bf-cell-sub'}`} style={{ textAlign: 'right' }}>
                        {r.missingOut || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      <p className="bf-cell-sub">“Sin salida”: días en que marcó la entrada y no la salida. Esas horas no se cuentan.</p>
      {open && (
        <Dialog
          open
          onOpenChange={(o) => !o && setOpen(null)}
          title={open.name}
          description={`Del ${formatDate(from)} al ${formatDate(to)} · ${hours(open.totalMinutes)}`}
          wide
        >
          <div className="bf-scroll-box bf-cut-table">
            <table className="bf-table">
              <tbody>
                {open.days.map((d) => (
                  <tr key={d.date}>
                    <td className="bf-cell-main">{formatDate(d.date)}</td>
                    <td>
                      {d.shifts.map((s, i) => (
                        <span key={i} style={{ marginRight: 12 }}>
                          {s.in ?? '—'} a {s.out ?? <Badge tone="warning">sin salida</Badge>}
                        </span>
                      ))}
                    </td>
                    <td className="bf-num" style={{ textAlign: 'right' }}>
                      {d.minutes ? hours(d.minutes) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Dialog>
      )}
    </>
  );
}
